import { ChangeEvent, FormEvent } from 'react';
import { LockKeyhole, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProfileData } from '../hooks/useProfileData';
import { ProfileLeadFormValues, useProfileLeadForm } from '../hooks/useProfileLeadForm';

interface PublicProfileLeadFormProps {
  profile: ProfileData;
  tone?: 'light' | 'dark';
  showLoanDetails?: boolean;
  className?: string;
}

export function PublicProfileLeadForm({
  profile,
  tone = 'light',
  showLoanDetails = false,
  className
}: PublicProfileLeadFormProps) {
  const { values, status, updateValue, submit } = useProfileLeadForm();
  const dark = tone === 'dark';
  const fieldClass = cn(
    'min-h-11 w-full rounded-lg border px-3 py-2 text-base outline-none transition focus:ring-2 focus:ring-offset-2',
    dark
      ? 'border-white/15 bg-white/[0.06] text-white placeholder:text-slate-500 focus:border-cyan-300 focus:ring-cyan-300/30 focus:ring-offset-slate-950'
      : 'border-slate-300 bg-white text-slate-950 placeholder:text-slate-400 focus:border-primary focus:ring-primary/20 focus:ring-offset-white'
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  const setField = (field: keyof ProfileLeadFormValues) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => updateValue(field, event.target.value);

  return (
    <form
      onSubmit={onSubmit}
      className={cn(
        'rounded-2xl border p-6 shadow-xl sm:p-8',
        dark ? 'border-white/10 bg-slate-950/70' : 'border-slate-200 bg-white',
        className
      )}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <label className={cn('space-y-2 text-sm font-medium', dark ? 'text-slate-200' : 'text-slate-800')}>
          Full name
          <input
            name="name"
            value={values.name}
            onChange={setField('name')}
            className={fieldClass}
            autoComplete="name"
            maxLength={120}
            required
          />
        </label>
        <label className={cn('space-y-2 text-sm font-medium', dark ? 'text-slate-200' : 'text-slate-800')}>
          Phone
          <input
            name="phone"
            type="tel"
            value={values.phone}
            onChange={setField('phone')}
            className={fieldClass}
            autoComplete="tel"
            maxLength={32}
            required
          />
        </label>
        <label className={cn('space-y-2 text-sm font-medium sm:col-span-2', dark ? 'text-slate-200' : 'text-slate-800')}>
          Email
          <input
            name="email"
            type="email"
            value={values.email}
            onChange={setField('email')}
            className={fieldClass}
            autoComplete="email"
            maxLength={254}
            required
          />
        </label>
        {showLoanDetails && (
          <>
            <label className={cn('space-y-2 text-sm font-medium', dark ? 'text-slate-200' : 'text-slate-800')}>
              Estimated loan amount <span className="font-normal opacity-70">(optional)</span>
              <input
                name="loanAmount"
                inputMode="decimal"
                value={values.loanAmount}
                onChange={setField('loanAmount')}
                className={fieldClass}
                placeholder="$500,000"
              />
            </label>
            <label className={cn('space-y-2 text-sm font-medium', dark ? 'text-slate-200' : 'text-slate-800')}>
              Property type <span className="font-normal opacity-70">(optional)</span>
              <select
                name="propertyType"
                value={values.propertyType}
                onChange={setField('propertyType')}
                className={fieldClass}
              >
                <option value="">Select one</option>
                <option value="Single family">Single family</option>
                <option value="Condominium">Condominium</option>
                <option value="Townhome">Townhome</option>
                <option value="Multi-unit">Multi-unit</option>
                <option value="Other">Other</option>
              </select>
            </label>
          </>
        )}
        <label className={cn('space-y-2 text-sm font-medium sm:col-span-2', dark ? 'text-slate-200' : 'text-slate-800')}>
          How can {profile.firstName || 'this loan officer'} help?
          <textarea
            name="message"
            value={values.message}
            onChange={setField('message')}
            className={cn(fieldClass, 'min-h-32 resize-y')}
            maxLength={2000}
            required
          />
        </label>
      </div>

      <div className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label>
          Website
          <input
            name="website"
            value={values.website}
            onChange={setField('website')}
            tabIndex={-1}
            autoComplete="off"
          />
        </label>
      </div>

      {/* Present from the first render so the note is announced when it appears. */}
      <div role="status">
        {status === 'preview' && (
          <p className={cn('mt-4 text-sm font-medium', dark ? 'text-slate-200' : 'text-slate-700')}>
            Preview only. Nothing was sent. Visitors send messages from your public page.
          </p>
        )}
      </div>

      <button
        type="submit"
        className={cn(
          'mt-6 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2',
          dark
            ? 'bg-cyan-300 text-slate-950 hover:bg-cyan-200 focus:ring-cyan-300 focus:ring-offset-slate-950'
            : 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-primary focus:ring-offset-white'
        )}
      >
        <Send className="h-4 w-4" aria-hidden="true" />
        Send message
      </button>
      <p className={cn('mt-4 flex items-start justify-center gap-2 text-xs', dark ? 'text-slate-500' : 'text-slate-500')}>
        <LockKeyhole className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Your information is used to respond to this request and is not treated as marketing consent.
      </p>
    </form>
  );
}
